import React, { useMemo } from "react";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  styled,
  Tooltip,
  Typography,
} from "@mui/material";
import { ICampaignPreferencesDetails } from "./CampaignSetupForm";
import { COLOR_MAP } from "../../../../../constants/ColorNames";
import { INFO_ICON } from "../../../../../constants/icons/InfoIcon";
import {
  getIsTriggeredFromFolder,
} from "../../../../../utils/whatsAppUtils";
import CreditMsg from "./CreditMsg";
import { campaignMaxLimitSelector } from "../../../../../redux/selectors/user.selector";
import { useSelector } from "react-redux";

interface IProps extends ICampaignPreferencesDetails {}


const ToolTipContainer = styled(Grid)`
  width: 440px;
  padding: 15px;
  color: ${COLOR_MAP.WHITE};
  background-color: ${COLOR_MAP.LIGHT_GREY};
  border-radius: 1rem;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`;

const CampaignPreferences: React.FC<IProps> = ({
  handleUpdateIsSendToAlreadyContacted,
  isSendToAlreadyContacted,
  isBulkCampaign,
  triggeredFromPage,
}) => {
  const showCheckbox =
    isBulkCampaign && triggeredFromPage?.toLowerCase() === "srp";
  const showRadioButtons =
    isBulkCampaign && getIsTriggeredFromFolder(triggeredFromPage);

  const toggleSendMailToContactedProfiles = () => {
    handleUpdateIsSendToAlreadyContacted(!isSendToAlreadyContacted);
  };
  const campaignMaxLimit = useSelector(campaignMaxLimitSelector);

  const Options = useMemo(() => [
  {
    label: "All candidates",
    value: "All",
    infoText: `Select this option to send campaign to all candidates in the folder, max ${campaignMaxLimit} at once.`,
  },
  {
    label: "Only uncontacted candidates",
    value: "Non-contacted",
    infoText: `Select this option to send campaign to candidates in the folder who haven’t been targeted yet, max ${campaignMaxLimit} at once.`,
  },
], [campaignMaxLimit]);

  const handleRadioChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    if (value === "All") {
      handleUpdateIsSendToAlreadyContacted(true);
    } else {
      handleUpdateIsSendToAlreadyContacted(false);
    }
  };
  return (
    <Box sx={{ mt: "8px", display: showCheckbox ? "flex" : "inline-block" }}>
      {/* Show Checkbox when triggered from SRP(Search result page) and bulk campaign */}
      {showCheckbox && (
        <Box
          alignItems="center"
          display="flex"
          columnGap="0.5rem"
          onClick={toggleSendMailToContactedProfiles}
          sx={{
            cursor: "pointer",
            width: "max-content",
          }}
        >
          <Checkbox
            sx={{
              p: 0,
              "& svg": {
                color: `${COLOR_MAP.GREY_2} !important`,
                width: "1.75rem",
                height: "1.75rem",
              },
            }}
            checked={isSendToAlreadyContacted}
          />
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: "1.2rem",
              lineHeight: "1.6rem",
              color: `${COLOR_MAP.LIGHT_GREY}`,
              margin: 0,
            }}
          >
            Send campaign to existing profiles in the folder
          </Typography>

          <Tooltip
            title={
              <ToolTipContainer container spacing={1}>
                <Typography variant="h6">
                  Select this option to send campaign to profiles that already
                  exist in the selected folder.
                </Typography>
              </ToolTipContainer>
            }
            arrow
            placement="top-start"
          >
            <Box sx={{ mt: 0.5 }}>
              <INFO_ICON height={14} width={14} />
            </Box>
          </Tooltip>
        </Box>
      )}

      {/* Radio Buttons - Show when triggered from Folders page and bulk campaign  */}
      {showRadioButtons && (
        <FormControl sx={{ display: "flex", flexDirection: "row" }}>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: "1.2rem",
              lineHeight: "1.6rem",
              color: `${COLOR_MAP.LIGHT_GREY}`,
              mr: 1,
            }}
          >
            Send campaign to:
          </Typography>
          <RadioGroup
            aria-labelledby="Send Message Preference Radio button"
            name="radio-buttons-group"
            value={isSendToAlreadyContacted ? "All" : "Non-contacted"}
            onChange={handleRadioChange}
            sx={{ display: "flex", flexFlow: "unset" }}
          >
            {Options?.map((option, index) => (
              <Box
                key={option.value}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  value={option.value}
                  control={
                    <Radio
                      sx={{
                        color: `${COLOR_MAP.GREY_2} !important`,
                        p: 0,
                        px: "8px",
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: "1.2rem",
                        lineHeight: "1.6rem",
                        color: `${COLOR_MAP.LIGHT_GREY}`,
                      }}
                    >
                      {option.label}
                    </Typography>
                  }
                  sx={{
                    mr: 1,
                  }}
                />
              </Box>
            ))}
          </RadioGroup>

          <Tooltip
            title={
              <ToolTipContainer container spacing={1}>
                <Typography variant="h6">
                  <b>All Candidates</b> - Select this option to send campaign to
                  all candidates in the folder, max {campaignMaxLimit} at once.
                </Typography>
                <br />
                <Typography variant="h6">
                  <b>Uncontacted Candidates</b> - Select this option to send
                  campaign to candidates in the folder who haven’t been targeted
                  yet, max {campaignMaxLimit} at once.
                </Typography>
              </ToolTipContainer>
            }
            arrow
            placement="top-start"
          >
            <Box sx={{ mt: 0.5 }}>
              <INFO_ICON height={14} width={14} />
            </Box>
          </Tooltip>
        </FormControl>
      )}

      {showCheckbox && (
        <Box sx={{ mx: 2 }}>
          <Divider orientation="vertical" />
        </Box>
      )}
      <CreditMsg sx={{ mt: showCheckbox ? "0px" : "16px" }} />
    </Box>
  );
};

export default CampaignPreferences;
